import React from 'react';
import {StyleSheet, View} from 'react-native';
import {Formik} from 'formik';
import * as Yup from 'yup';

import {Address} from '../../../models/middleware';
import Button from '../Button';
import TextInput from '../form/TextInput';
import ValidationMessage from '../form/ValidationMessage';

import SuburbPicker from './SuburbPicker';

// Based on MFL address but done manually to not complicate things with
// line1/line2 and the remaining MFL fields. Also, working with
// Yup.SchemaOf<Address> seems to be a pain at least with the current version
// of Yup.
const ManualAddressEntrySchema = Yup.object({
  line1: Yup.string().required('Unit/Street number is required'),
  line2: Yup.string(),
  locality: Yup.string().required('Suburb is required'),
  postal_code: Yup.string().required('Postcode is required'),
  administrative_area: Yup.string().required('State is required'),
});
type ManualAddressEntryValues = Yup.InferType<typeof ManualAddressEntrySchema>;

type Props = {
  address?: Address;
  onChange: (address: Address) => void;
  onCancel: () => void;
};

const ManualAddressEntry = ({address, onChange, onCancel}: Props) => {
  const defaultValues: ManualAddressEntryValues = {
    line1: address?.lines?.[0] || '',
    line2: address?.lines?.[1] || '',
    postal_code: address?.postal_code || '',
    locality: address?.locality || '',
    administrative_area: address?.administrative_area || '',
  };

  const initialSuburb = {
    locality: address?.locality || '',
    postal_code: address?.postal_code || '',
    administrative_area: address?.administrative_area || '',
  };

  const onSubmit = (enteredAddress: ManualAddressEntryValues) => {
    const lines = [];

    if (enteredAddress.line1 && enteredAddress.line1.trim() !== '') {
      lines.push(enteredAddress.line1);
    }

    if (enteredAddress.line2 && enteredAddress.line2.trim() !== '') {
      lines.push(enteredAddress.line2);
    }

    onChange({
      lines,
      postal_code: enteredAddress.postal_code,
      locality: enteredAddress.locality,
      administrative_area: enteredAddress.administrative_area,
    });
  };

  return (
    <Formik<ManualAddressEntryValues>
      initialValues={defaultValues}
      onSubmit={onSubmit}
      validationSchema={ManualAddressEntrySchema}>
      {({
        errors,
        handleBlur,
        handleChange,
        initialValues,
        handleSubmit,
        touched,
        setFieldValue,
        values,
      }) => (
        <View style={styles.container}>
          <View style={styles.form}>
            <TextInput
              autoCapitalize="none"
              autoComplete="off"
              defaultValue={initialValues.line1}
              errorMessageWhenTouched={errors.line1}
              onBlur={handleBlur('line1')}
              onChangeText={handleChange('line1')}
              placeholder="Unit/Street number"
              touched={touched.line1}
            />
            <TextInput
              autoCapitalize="none"
              autoComplete="off"
              defaultValue={initialValues.line2}
              errorMessageWhenTouched={errors.line2}
              onBlur={handleBlur('line2')}
              onChangeText={handleChange('line2')}
              placeholder="Address Line 2"
              touched={touched.line2}
            />
            <SuburbPicker
              initialSuburb={initialSuburb}
              onSelectSuburb={suburb => {
                setFieldValue('locality', suburb.locality);
                setFieldValue('postal_code', suburb.postal_code);
                setFieldValue(
                  'administrative_area',
                  suburb.administrative_area,
                );
              }}
            />
            {!!errors.locality && !!touched.locality ? (
              <ValidationMessage>This is a required field</ValidationMessage>
            ) : !!errors.postal_code && !!touched.postal_code ? (
              <ValidationMessage>This is a required field</ValidationMessage>
            ) : !!errors.administrative_area &&
              !!touched.administrative_area ? (
              <ValidationMessage>This is a required field</ValidationMessage>
            ) : null}
          </View>
          <View style={styles.buttons}>
            <Button onPress={handleSubmit as any} title="Use this address" />
            <Button
              onPress={onCancel}
              style={styles.cancelButton}
              title="Cancel"
              type="outline"
            />
          </View>
        </View>
      )}
    </Formik>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  form: {
    flex: 1,
  },
  buttons: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginBottom: 16,
    zIndex: -1, // the dropdown zindex isn't respected, so this lets the dropdown sit over the buttons
  },
  cancelButton: {
    marginTop: 8,
  },
});

export default ManualAddressEntry;
